<script lang="ts">
	import FlexibleImageCollage from '$lib/components/ui/FlexibleImageCollage.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import { CITY_FEATURE_TYPE_SINGLE_LABELS } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { ClassValue } from 'svelte/elements';
	import IconArrowRight from '~icons/mdi/arrow-right';

	const {
		feature,
		images,
		location,
		ctaText,
		descriptionLineClampNumber = 3
	}: {
		feature: CityFeaturePreview;
		images: CoverImage[];
		location: PlaceLocation;
		ctaText?: string;
		descriptionLineClampNumber?: 2 | 3;
	} = $props();

	const featureNav = $derived<NavLink>(UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug }));
	const label = $derived<string>(CITY_FEATURE_TYPE_SINGLE_LABELS[feature.type]);
	const descriptionLineClampClass = $derived.by<ClassValue>(() => {
		switch (descriptionLineClampNumber) {
			case 2:
				return 'line-clamp-2';
			case 3:
				return 'line-clamp-3';
		}
	});
</script>

<a
	class="group relative flex h-full flex-col overflow-hidden rounded-box border border-base-200 bg-base-200 shadow-sm transition-all duration-300 hover:-translate-y-1"
	title="View {feature.englishName}"
	href={`/${featureNav.href}`}
	data-sveltekit-preload-data={featureNav.preloadData}
	data-sveltekit-preload-code={featureNav.preloadCode}
>
	<div class="relative h-[33vh] w-full overflow-hidden">
		<FlexibleImageCollage {images} isEagerLoad={false} />
	</div>

	<div class="flex flex-1 flex-col gap-3 p-5">
		<h2 class="line-clamp-1 text-lg transition-colors group-hover:text-primary">
			{feature.englishName}
		</h2>

		<p class="text-sm text-base-content/70 {descriptionLineClampClass}">
			{feature.description}
		</p>

		<div class="mt-auto flex items-center justify-between border-t border-base-content/5 pt-4">
			{@render Footer()}
		</div>
	</div>
</a>

{#snippet Footer()}
	<div class="mt-auto card-actions w-full justify-center pt-2 md:justify-end">
		<span class="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-primary">
			{#if ctaText}
				{ctaText}
			{:else}
				view {label.toLowerCase()}
			{/if}
			<IconArrowRight class="transition-transform group-hover:translate-x-1" />
		</span>
	</div>
{/snippet}
